/**
 * Search & Matching Utility
 *
 * Provides token-based multi-word search, punctuation normalization,
 * typo/fuzzy matching (Damerau-Levenshtein), and relevance scoring for sheets and files.
 */

/**
 * Normalizes text for search:
 * - Lowercases
 * - Strips accents/diacritics (e.g. 'é' -> 'e')
 * - Replaces punctuation and special characters with spaces
 * - Collapses consecutive whitespace and trims
 */
export function normalizeSearchText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/[-_/:\\|()[\]{}.,;'"+*&^%$#@!~?<>`=]/g, ' ') // replace punctuation with space
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Compact representation with all non-alphanumeric characters stripped.
 * Useful for matching things like 'doc-128' with 'doc128' or 'super-admin' with 'superadmin'.
 */
export function compactSearchText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Computes Damerau-Levenshtein distance between two strings
 * Handles insertions, deletions, substitutions, and adjacent transpositions.
 */
export function damerauLevenshteinDistance(source: string, target: string): number {
  if (source === target) return 0;
  if (!source.length) return target.length;
  if (!target.length) return source.length;

  const sourceLength = source.length;
  const targetLength = target.length;
  const matrix: number[][] = [];

  for (let i = 0; i <= sourceLength; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= targetLength; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= sourceLength; i++) {
    const sChar = source.charAt(i - 1);
    for (let j = 1; j <= targetLength; j++) {
      const tChar = target.charAt(j - 1);
      const cost = sChar === tChar ? 0 : 1;

      let min = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );

      // Transposition
      if (
        i > 1 &&
        j > 1 &&
        source.charAt(i - 1) === target.charAt(j - 2) &&
        source.charAt(i - 2) === target.charAt(j - 1)
      ) {
        min = Math.min(min, matrix[i - 2][j - 2] + cost);
      }

      matrix[i][j] = min;
    }
  }

  return matrix[sourceLength][targetLength];
}

/**
 * Checks whether a single query token matches a target word either exactly,
 * via prefix, via substring, or via typo tolerance (Damerau-Levenshtein).
 */
export function isTokenMatch(
  queryToken: string,
  targetWord: string
): { matched: boolean; isExact: boolean; isPrefix: boolean; isFuzzy: boolean; distance: number } {
  if (queryToken === targetWord) {
    return { matched: true, isExact: true, isPrefix: true, isFuzzy: false, distance: 0 };
  }

  // Prefix match (e.g. 'dai' matches 'daily')
  if (targetWord.startsWith(queryToken)) {
    return { matched: true, isExact: false, isPrefix: true, isFuzzy: false, distance: 0 };
  }

  // Substring match (e.g. 'admin' matches 'superadmin')
  if (targetWord.includes(queryToken)) {
    return { matched: true, isExact: false, isPrefix: false, isFuzzy: false, distance: 0 };
  }

  // Reverse prefix (e.g. user typed 'superadmin' and target word is 'super')
  if (queryToken.startsWith(targetWord) && targetWord.length >= 4) {
    return { matched: true, isExact: false, isPrefix: true, isFuzzy: false, distance: 0 };
  }

  // Typo tolerance:
  // - For 1-2 char tokens: require exact prefix or substring (no fuzzy to avoid noise)
  // - For 3-4 char tokens: allow distance <= 1 (e.g. 'daik' -> 'dail')
  // - For 5+ char tokens: allow distance <= 2 (e.g. 'superamind' -> 'superadmin')
  const qLen = queryToken.length;
  const tLen = targetWord.length;

  if (qLen >= 3 && Math.abs(qLen - tLen) <= 2) {
    const maxDist = qLen >= 5 ? 2 : 1;
    const dist = damerauLevenshteinDistance(queryToken, targetWord);
    if (dist <= maxDist) {
      return { matched: true, isExact: false, isPrefix: false, isFuzzy: true, distance: dist };
    }
  }

  return { matched: false, isExact: false, isPrefix: false, isFuzzy: false, distance: 99 };
}

export interface SearchFieldWeights {
  primary?: string | (string | undefined | null)[];   // e.g. Sheet/Document Title
  secondary?: string | (string | undefined | null)[]; // e.g. Description, Tab Names, Doc Number
  tertiary?: string | (string | undefined | null)[];  // e.g. Team, Categories
}

/**
 * Checks whether an item matches the search query and returns relevance score.
 * If query is empty, returns { matched: true, score: 0 }.
 */
export function evaluateSearchMatch(
  query: string | null | undefined,
  fields: SearchFieldWeights
): { matched: boolean; score: number } {
  if (!query || !query.trim()) {
    return { matched: true, score: 0 };
  }

  const rawQuery = query.trim();
  const normalizedQuery = normalizeSearchText(rawQuery);
  const compactQuery = compactSearchText(rawQuery);
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);

  if (queryTokens.length === 0) {
    return { matched: true, score: 0 };
  }

  const flattenField = (val: string | (string | undefined | null)[] | undefined): string => {
    if (!val) return '';
    if (Array.isArray(val)) {
      return val.filter(Boolean).join(' ');
    }
    return String(val);
  };

  const primaryText = flattenField(fields.primary);
  const secondaryText = flattenField(fields.secondary);
  const tertiaryText = flattenField(fields.tertiary);

  const normPrimary = normalizeSearchText(primaryText);
  const normSecondary = normalizeSearchText(secondaryText);
  const normTertiary = normalizeSearchText(tertiaryText);

  const compactPrimary = compactSearchText(primaryText);
  const compactSecondary = compactSearchText(secondaryText);
  const compactTertiary = compactSearchText(tertiaryText);

  const primaryWords = normPrimary.split(' ').filter(Boolean);
  const secondaryWords = normSecondary.split(' ').filter(Boolean);
  const tertiaryWords = normTertiary.split(' ').filter(Boolean);

  const combinedNorm = `${normPrimary} ${normSecondary} ${normTertiary}`;
  const combinedCompact = `${compactPrimary} ${compactSecondary} ${compactTertiary}`;

  let score = 0;

  // 1. Exact full-phrase matches
  if (normPrimary === normalizedQuery) {
    score += 1500; // Perfect exact match on title
  } else if (normPrimary.startsWith(normalizedQuery)) {
    score += 800; // Title starts with full search phrase
  } else if (normPrimary.includes(normalizedQuery)) {
    score += 500; // Full search phrase found inside title
  } else if (combinedNorm.includes(normalizedQuery)) {
    score += 300; // Full search phrase found across combined text
  } else if (compactPrimary.includes(compactQuery)) {
    score += 400; // Compact match in title (e.g. 'doc128' in 'doc-128')
  } else if (combinedCompact.includes(compactQuery)) {
    score += 250; // Compact match in combined
  }

  // 2. Token-by-token matching
  // Every token in the query MUST match somewhere (exact, prefix, substring, or fuzzy)
  let matchedTokensCount = 0;

  for (const qToken of queryTokens) {
    let tokenMatched = false;
    let bestTokenScore = 0;

    // Check against primary words first
    for (const w of primaryWords) {
      const match = isTokenMatch(qToken, w);
      if (match.matched) {
        tokenMatched = true;
        const pts = match.isExact ? 120 : match.isPrefix ? 90 : match.isFuzzy ? 60 - match.distance * 10 : 70;
        if (pts > bestTokenScore) bestTokenScore = pts;
      }
    }

    // Check against secondary words
    if (!tokenMatched || bestTokenScore < 100) {
      for (const w of secondaryWords) {
        const match = isTokenMatch(qToken, w);
        if (match.matched) {
          tokenMatched = true;
          const pts = match.isExact ? 80 : match.isPrefix ? 60 : match.isFuzzy ? 40 - match.distance * 10 : 50;
          if (pts > bestTokenScore) bestTokenScore = pts;
        }
      }
    }

    // Check against tertiary words (teams, categories)
    if (!tokenMatched || bestTokenScore < 60) {
      for (const w of tertiaryWords) {
        const match = isTokenMatch(qToken, w);
        if (match.matched) {
          tokenMatched = true;
          const pts = match.isExact ? 40 : match.isPrefix ? 30 : match.isFuzzy ? 20 - match.distance * 10 : 25;
          if (pts > bestTokenScore) bestTokenScore = pts;
        }
      }
    }

    // Substring fallback on full normalized strings
    if (!tokenMatched) {
      if (normPrimary.includes(qToken)) {
        tokenMatched = true;
        bestTokenScore = Math.max(bestTokenScore, 65);
      } else if (normSecondary.includes(qToken)) {
        tokenMatched = true;
        bestTokenScore = Math.max(bestTokenScore, 45);
      } else if (normTertiary.includes(qToken)) {
        tokenMatched = true;
        bestTokenScore = Math.max(bestTokenScore, 25);
      }
    }

    // Compact token fallback (e.g. '128' or 'doc128')
    if (!tokenMatched && qToken.length >= 3) {
      const cToken = compactSearchText(qToken);
      if (cToken && combinedCompact.includes(cToken)) {
        tokenMatched = true;
        bestTokenScore = Math.max(bestTokenScore, 40);
      }
    }

    if (tokenMatched) {
      matchedTokensCount++;
      score += bestTokenScore;
    }
  }

  // All query tokens must match to qualify as a match
  const allTokensMatched = matchedTokensCount === queryTokens.length;

  if (allTokensMatched) {
    return { matched: true, score };
  }

  // Possible match tolerance: If query has 3+ words and at least 2 or 75% match,
  // allow it as a possible match with lower score
  if (queryTokens.length >= 3 && matchedTokensCount >= Math.ceil(queryTokens.length * 0.75)) {
    return { matched: true, score: Math.round(score * 0.5) };
  }

  return { matched: false, score: 0 };
}

/**
 * Simple convenience wrapper returning boolean whether an item matches query.
 */
export function matchesSearch(
  query: string | null | undefined,
  fields: SearchFieldWeights
): boolean {
  return evaluateSearchMatch(query, fields).matched;
}
